import React from 'react';

import type { SuggestedEditKind } from '@aline/common';
import { Select } from '@aline/common';
import type { useSortable } from '@dnd-kit/sortable';
import { IconButton } from '@mui/material';
import { GripVertical, Trash2, TriangleAlert } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import {
  OrdinalBadge,
  SuggestedTextDiff,
  SUGGESTION_CARD_TONES,
  SUGGESTION_TONES,
  SuggestionChip,
} from './suggested-edits';

/** The column left of a rule's box holding its number: the 22px badge plus an 8px gap to the box. */
export const RULE_GUTTER_PX = 30;

/** The label's line height and vertical padding: what one line of the name takes up inside the box. */
const LABEL_LINE_HEIGHT_PX = 22;
const LABEL_PADDING_Y_PX = 6;
/** The height of the name line when the name fits on one line; the controls centre on it. */
const NAME_LINE_HEIGHT_PX = LABEL_LINE_HEIGHT_PX + 2 * LABEL_PADDING_Y_PX;

/** The resting border of a rule's box: black at 15%, about the card gray on white. */
const RULE_BORDER = 'rgba(0, 0, 0, 0.15)';
/** The selection tint for a rule with no suggestion. */
const RULE_SELECTED_BACKGROUND = 'rgba(20, 110, 232, 0.08)';

const useStyles = makeStyles<
  {
    containerKind: SuggestedEditKind | null;
    dragging: boolean;
    /** The row has the number gutter: every row in the list, and the floating copy, which leaves it empty. */
    hasGutter: boolean;
    kind: SuggestedEditKind | null;
    overlay: boolean;
    selected: boolean;
  },
  'box' | 'deleteButton' | 'dragHandleIcon'
>()((theme, { containerKind, dragging, hasGutter, kind, overlay, selected }, classes) => {
  /**
   * A rule being added or removed as a whole is tinted at rest like a
   * position card of that kind: green for an add, red for a delete, under
   * the kind's border. Selected, it keeps the tint and takes the card's
   * darker current border. A row that is only edited has no tint of its own
   * and selects like a plain row; its kind already shows on its chip. One
   * inside a card tinted as a whole takes that card's border. A row holding
   * the current stop looks like any other selected row: the stop itself is
   * marked in the details panel.
   */
  const hasOwnSuggestion = Boolean(kind);
  const wholeTone = kind === 'add' || kind === 'delete' ? SUGGESTION_CARD_TONES[kind] : null;
  const containerTone = containerKind && !hasOwnSuggestion ? SUGGESTION_TONES[containerKind] : null;
  const borderColor = containerKind ? SUGGESTION_CARD_TONES[containerKind].border : RULE_BORDER;
  const restingBackground = wholeTone?.background ?? 'transparent';
  const restingBorder = wholeTone?.border ?? borderColor;
  const selectedBackground =
    wholeTone?.background ?? containerTone?.background ?? RULE_SELECTED_BACKGROUND;
  const selectedBorder =
    wholeTone?.currentBorder ?? (containerTone ? borderColor : theme.palette.primary.main);
  /**
   * A selected row, a tinted one, or one in a tinted card hovers in its tint
   * so it does not go gray under the pointer and then coloured on the click.
   */
  const keepsTintOnHover = selected || Boolean(wholeTone) || Boolean(containerTone);
  const hoverBackground = keepsTintOnHover ? selectedBackground : theme.appColors.hoverBackground;

  return {
    /**
     * The tinted, bordered box: the name line, and under it any footer the
     * caller adds (a rule's pending move). Hover is driven from the wrapper so
     * reaching for the grip in the card padding still highlights the row it
     * belongs to.
     */
    box: {
      backgroundColor: selected ? selectedBackground : restingBackground,
      border: `1px solid ${selected ? selectedBorder : restingBorder}`,
      borderRadius: 6,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    /**
     * The floating copy under the pointer: lifted off the page, and opaque so
     * the rows it passes over do not show through.
     */
    boxOverlay: {
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[4],
    },
    /**
     * The chip, or the delete button, at the right end of the name line.
     * Pinned to the top of the line and given the height of a single-line
     * name, with the control centred inside, so when a title diff wraps onto a
     * second line the control stays centred on the first.
     */
    controls: {
      alignItems: 'center',
      alignSelf: 'flex-start',
      display: 'inline-flex',
      flexShrink: 0,
      height: NAME_LINE_HEIGHT_PX,
    },
    /**
     * Both controls are revealed on row hover so each row reads as a name rather
     * than a name between two icons. Keyboard focus reveals them too, but via
     * :focus-visible rather than :focus-within, because clicking the label focuses it
     * and the plain focus would otherwise pin the icons on the selected row.
     */
    deleteButton: {
      color: theme.appColors.secondaryType,
      flexShrink: 0,
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    /**
     * Sits in the category card's left padding, outside the tinted row but still
     * inside the card. The gutter is the card's side padding (theme.spacing(2)).
     */
    dragHandle: {
      alignItems: 'center',
      bottom: 0,
      color: theme.appColors.secondaryType,
      cursor: dragging || overlay ? 'grabbing' : 'grab',
      display: 'flex',
      justifyContent: 'center',
      left: `calc(-1 * ${theme.spacing(2)})`,
      position: 'absolute',
      top: 0,
      touchAction: 'none',
      width: theme.spacing(2),
    },
    // Only the glyph fades, so the hit area stays in place at rest.
    dragHandleIcon: {
      opacity: overlay ? 1 : 0,
      transition: 'opacity 120ms ease',
    },
    /**
     * A button rather than a field: the rule's name is edited in the details
     * panel, so the row only has to open it. Unstyled so the row around it
     * carries the selected state.
     */
    label: {
      background: 'none',
      border: 0,
      color: theme.appColors.iconsType,
      cursor: 'pointer',
      flexGrow: 1,
      font: 'inherit',
      fontSize: 14,
      lineHeight: `${LABEL_LINE_HEIGHT_PX}px`,
      minWidth: 0,
      overflow: 'hidden',
      padding: `${LABEL_PADDING_Y_PX}px 0`,
      textAlign: 'left',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    /**
     * The name and its controls. A plain name never wraps, but a pending
     * title diff does, so the controls align to the top and centre themselves
     * on the first line rather than on the whole label.
     */
    line: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0, 1),
    },
    /**
     * The number sits in a gutter left of the box, outside its tint and border,
     * centred on the name line rather than on the whole box so it stays put
     * when a footer makes the box taller. The height is the name line's: the
     * label's padding and line height plus the box border.
     */
    ordinal: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-end',
      // The name line plus the box's top border.
      minHeight: NAME_LINE_HEIGHT_PX + 1,
      paddingRight: 8,
    },
    /**
     * Anchors the grip in the card padding and lays the number beside the box.
     * The wrapper itself has no tint; that lives on the box so the grip and the
     * number sit outside the selected/hover background. The floating drag copy
     * has no number but keeps the gutter, empty: the drop animation sends the
     * copy to the row's rectangle, gutter included, so without it the copy's
     * box would land a gutter's width left of the real box and then snap.
     */
    row: {
      [`&:has(.${classes.deleteButton}:focus-visible) .${classes.deleteButton}, &:hover .${classes.deleteButton}, &:has(.${classes.deleteButton}:focus-visible) .${classes.dragHandleIcon}, &:hover .${classes.dragHandleIcon}`]:
        {
          opacity: 1,
        },
      [`&:hover .${classes.box}`]: {
        backgroundColor: hoverBackground,
      },
      alignItems: 'start',
      display: 'grid',
      gridTemplateColumns: hasGutter ? `${RULE_GUTTER_PX}px minmax(0, 1fr)` : 'minmax(0, 1fr)',
      /**
       * The row left behind in the list while its copy floats, so the gap
       * shows where the drop would land.
       */
      opacity: dragging ? 0.4 : 1,
      position: 'relative',
      width: '100%',
    },
    // A rule proposed for removal: struck through, in the delete chip's red.
    // Always shown, unlike the hover-revealed controls: a rule that will not run should not hide it.
    runWarning: {
      color: theme.palette.warning.main,
      flexShrink: 0,
    },
    struck: {
      color: SUGGESTION_TONES.delete.color,
      textDecorationLine: 'line-through',
    },
    untitled: {
      color: theme.appColors.secondaryType,
    },
  };
});

/** What `useSortable` hands back for the element that starts a drag. */
type DragListeners = ReturnType<typeof useSortable>['listeners'];

/**
 * The suggestion a row carries, as the row renders it. The row only names the
 * kind: every suggestion inside a rule is accepted or rejected in the details
 * panel, which opens when the row is clicked.
 */
export interface RuleRowSuggestion {
  kind: SuggestedEditKind;
  /** Why the AI proposed it: the rule's own reason, or one per position edit the chip stands for. */
  rationales: readonly string[];
  /** A pending title patch, rendered in place as a diff. */
  titleDiff?: { after: string; before: string };
}

interface RuleRowProps {
  /**
   * The kind the surrounding category card is tinted with as a whole (an add
   * or a delete), so the row's resting border and selection colours match
   * the card instead of the white page.
   */
  containerKind?: SuggestedEditKind | null;
  dataCy?: string;
  deleteDisabled?: boolean;
  /** Dims the row: it is the one being dragged, and a copy of it is floating. */
  dragging?: boolean;
  /** Listeners for the grip. Absent on the floating copy. */
  dragListeners?: DragListeners;
  /** Shown in place of the title while the rule has none. */
  fallbackLabel?: string;
  /** Rendered inside the box under the name line, e.g. a pending move. */
  footer?: React.ReactNode;
  /** Absent on the floating copy, whose controls are only there to match. */
  onDelete?: () => void;
  onSelect?: () => void;
  /** One-based place in the category, ghost destinations included. */
  ordinal?: number;
  /** Styles the row as the floating copy under the pointer. */
  overlay?: boolean;
  rootRef?: (node: HTMLElement | null) => void;
  /** Marks the row as this rule's in the DOM, so the page can scroll to it. Absent on the floating copy. */
  ruleId?: string;
  /** Why a run skips this rule, when it does. Shown as a warning triangle beside the name. */
  runWarning?: string | null;
  selected?: boolean;
  style?: React.CSSProperties;
  suggestion?: RuleRowSuggestion | null;
  title?: string | null;
}

export function RuleRow({
  containerKind = null,
  dataCy,
  deleteDisabled = false,
  dragListeners,
  dragging = false,
  fallbackLabel = 'Untitled rule',
  footer,
  onDelete,
  onSelect,
  ordinal,
  overlay = false,
  rootRef,
  ruleId,
  runWarning = null,
  selected = false,
  style,
  suggestion = null,
  title,
}: RuleRowProps): JSX.Element {
  const { classes, cx } = useStyles({
    containerKind,
    dragging,
    hasGutter: ordinal !== undefined || overlay,
    kind: suggestion?.kind ?? null,
    overlay,
    selected,
  });
  // A title of nothing but spaces is a rule nobody has named, the same as an
  // empty one — the create path trims, but a row renders what it is given.
  const name = title?.trim();
  const isStruck = suggestion?.kind === 'delete';

  return (
    <div ref={rootRef} className={classes.row} data-cy={dataCy} data-rule-id={ruleId} style={style}>
      <span
        className={classes.dragHandle}
        data-cy={Select.playbooks.ruleDragHandle}
        {...dragListeners}
      >
        <GripVertical className={classes.dragHandleIcon} size={16} />
      </span>
      {ordinal !== undefined && (
        <span className={classes.ordinal}>
          <OrdinalBadge index={ordinal} />
        </span>
      )}
      {ordinal === undefined && overlay && <span className={classes.ordinal} />}
      <div className={cx(classes.box, overlay && classes.boxOverlay)}>
        <div className={classes.line}>
          <button
            aria-pressed={selected}
            className={cx(classes.label, !name && classes.untitled, isStruck && classes.struck)}
            data-cy={Select.playbooks.ruleSelect}
            type="button"
            onClick={onSelect}
          >
            {suggestion?.titleDiff ? (
              <SuggestedTextDiff
                after={suggestion.titleDiff.after}
                before={suggestion.titleDiff.before}
              />
            ) : (
              name || fallbackLabel
            )}
          </button>
          {Boolean(runWarning) && (
            <span className={classes.controls}>
              <AlineTooltip arrow placement="top" title={runWarning}>
                <TriangleAlert
                  className={classes.runWarning}
                  data-cy={Select.playbooks.ruleRunWarning}
                  size={16}
                />
              </AlineTooltip>
            </span>
          )}
          {/* While a suggestion is pending on the rule the chip takes the delete
              button's place at the right edge. It has no accept/reject of its
              own: those are in the details panel, beside what they decide. */}
          {suggestion && (
            <span className={classes.controls}>
              <SuggestionChip kind={suggestion.kind} rationales={suggestion.rationales} />
            </span>
          )}
          {/* A row inside a category that is itself the suggestion has nothing of
              its own to decide or delete: the category's decision covers it. */}
          {!suggestion && !containerKind && (
            <span className={classes.controls}>
              <AlineTooltip arrow placement="top" title="Delete rule">
                <IconButton
                  className={classes.deleteButton}
                  data-cy={Select.playbooks.ruleDelete}
                  disabled={deleteDisabled}
                  size="small"
                  onClick={onDelete}
                >
                  <Trash2 size={16} />
                </IconButton>
              </AlineTooltip>
            </span>
          )}
        </div>
        {footer}
      </div>
    </div>
  );
}
